import 'react-datepicker/dist/react-datepicker.css';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { API } from '../../Common/Api';
import CommonTable from '../../Common/CommonTable';
import { ErrorToast, SuccessToast, WarningToast } from '../../Utils/toast';
import { RotateCcw, ChevronLeft, ChevronRight, Download, FileSpreadsheet, FileText, Loader2, Search } from 'lucide-react';
import CustomSelectInput from '../../Commponent/SelectInput';
import TextInput from '../../Commponent/TextInput';
import { downloadStatementExcel, downloadStatementPdf } from '../../Utils/statementExport';
import { pageSizeOptions } from '../../Utils/pagination';
import { getRole } from '../../Utils/auth';

// Statement downloads pull every row matching the current filters, not just the
// visible page, so they are paged through in bulk.
const EXPORT_PAGE_SIZE = 500;
const EXPORT_MAX_PAGES = 20;

// Partner new debit transactions. Admin sees everything; distributor/dealer/agent
// each see ONLY their own subtree (enforced on the backend). The org dropdowns are
// shown only where they make sense for the role.
const TransactionsN = () => {
  const role = getRole();
  const ownId = (() => { try { return JSON.parse(localStorage.getItem('UserDetail'))?._id || ''; } catch (e) { return ''; } })();
  const isAdmin = role === 'admin';
  const showDistributor = isAdmin;
  const showDealer = isAdmin || role === 'distributor';
  const showAgent = isAdmin || role === 'distributor' || role === 'dealer';

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // Guards against stale/out-of-order responses: only the latest request applies.
  const reqIdRef = useRef(0);

  // Filters — no default date range (user picks; empty = all transactions).
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // Org filters (cascading: Distributor → Dealer → Agent).
  const [distributors, setDistributors] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedDistributor, setSelectedDistributor] = useState('');
  const [selectedDealer, setSelectedDealer] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');

  // Vehicle number is free text, so the value that drives the fetch is debounced.
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [debouncedVehicle, setDebouncedVehicle] = useState('');

  // Download-statement menu.
  const [menuOpen, setMenuOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const menuRef = useRef(null);

  const asList = (res) => (Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.data) ? res.data.data : []));

  // Load dropdown data scoped to the role (admin = everyone; distributor = own
  // dealers/agents; dealer = own agents; agent = none).
  useEffect(() => {
    (async () => {
      try {
        if (isAdmin) {
          const [d, dl, ag] = await Promise.all([
            API.GetDistributorNames(),
            API.GetAllDealersNames({}),
            API.GetAgentsNames({}),
          ]);
          setDistributors(asList(d));
          setDealers(asList(dl));
          setAgents(asList(ag));
        } else if (role === 'distributor') {
          const [dl, ag] = await Promise.all([
            API.GetAllDealersNames(ownId ? { distributorId: ownId } : {}),
            API.GetAgentsNames({}),
          ]);
          setDealers(asList(dl));
          setAgents(asList(ag));
        } else if (role === 'dealer') {
          const ag = await API.GetAgentsNames({});
          setAgents(asList(ag));
        }
      } catch (e) { /* non-blocking */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedVehicle(vehicleNumber.trim().toUpperCase()), 500);
    return () => clearTimeout(timer);
  }, [vehicleNumber]);

  // Dismiss the download menu on any click outside it.
  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Distributor changes → reload its dealers + agents, reset child selections.
  const handleDistributorChange = async (id) => {
    setSelectedDistributor(id);
    setSelectedDealer('');
    setSelectedAgent('');
    setCurrentPage(1);
    try {
      const [dl, ag] = await Promise.all([
        API.GetAllDealersNames(id ? { distributorId: id } : {}),
        API.GetAgentsNames(id ? { distributorId: id } : {}),
      ]);
      setDealers(asList(dl));
      setAgents(asList(ag));
    } catch (e) { /* ignore */ }
  };

  // Dealer changes → reload that dealer's agents, reset agent selection.
  const handleDealerChange = async (id) => {
    setSelectedDealer(id);
    setSelectedAgent('');
    setCurrentPage(1);
    try {
      const ag = await API.GetAgentsNames({ distributorId: selectedDistributor || '', dealerId: id || '' });
      setAgents(asList(ag));
    } catch (e) { /* ignore */ }
  };

  // The partner feed has shipped several envelope shapes; normalise them once.
  const extractRows = (res) => {
    if (res.data?.rows && Array.isArray(res.data.rows)) {
      return { list: res.data.rows, count: res.data.total ?? res.data.rows.length };
    }
    if (Array.isArray(res.data)) {
      return { list: res.data, count: res.pagination?.total || res.data.length };
    }
    if (res.data?.transactions && Array.isArray(res.data.transactions)) {
      return { list: res.data.transactions, count: res.data.total || res.data.transactions.length };
    }
    if (res.data?.data && Array.isArray(res.data.data)) {
      return { list: res.data.data, count: res.data.total || res.data.pagination?.total || res.data.data.length };
    }
    if (res.data?.resultObj && Array.isArray(res.data.resultObj)) {
      const list = Array.isArray(res.data.resultObj[0]) ? res.data.resultObj[0] : res.data.resultObj;
      return { list, count: typeof res.data.resultObj[1] === 'number' ? res.data.resultObj[1] : list.length };
    }
    return { list: [], count: 0 };
  };

  const fetchTransactions = async () => {
    const reqId = ++reqIdRef.current; // mark this as the latest request
    setLoading(true);
    try {
      const res = await API.GetNewDebitTransactions({
        from: fromDate ? moment(fromDate).format('YYYY-MM-DD') : '',
        to: toDate ? moment(toDate).format('YYYY-MM-DD') : '',
        page: currentPage,
        page_size: itemsPerPage,
        vehicle_number: debouncedVehicle,
        distributorId: selectedDistributor || (role === 'distributor' ? ownId : ''),
        dealerId: selectedDealer || (role === 'dealer' ? ownId : ''),
        agentId: selectedAgent || (role === 'agent' ? ownId : ''),
      });

      // A newer request started while this one was in flight → drop this result.
      if (reqId !== reqIdRef.current) return;

      if (res?.status === 200 && res?.success) {
        const { list: dataList, count: totalCount } = extractRows(res);

        setRows(dataList);
        setTotal(totalCount);
        setTotalPages(res.pagination?.totalPages || Math.max(1, Math.ceil(totalCount / itemsPerPage)));
      } else {
        setRows([]);
        setTotal(0);
        setTotalPages(1);
        if (res?.message) {
          const cleanMsg = typeof res.message === 'string' ? res.message.replace(/<[^>]*>?/gm, '').trim() : '';
          if (cleanMsg) ErrorToast(cleanMsg);
        }
      }
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      setRows([]);
      setTotal(0);
      setTotalPages(1);
      ErrorToast('Failed to fetch new debit transactions');
    } finally {
      // Only the latest request controls the loading state.
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, fromDate, toDate, debouncedVehicle, selectedDistributor, selectedDealer, selectedAgent]);

  const StatusBadge = ({ status }) => {
    const s = (status || '').toUpperCase();
    const base = 'px-2.5 py-1 rounded-full text-xs font-medium inline-block';
    if (s === 'COMPLETED' || s === 'SUCCESS' || s === 'SUCCESSFUL') {
      return <span className={`${base} bg-green-100 text-green-800 border border-green-200`}>Completed</span>;
    }
    if (s === 'PENDING' || s === 'IN_PROGRESS') {
      return <span className={`${base} bg-yellow-100 text-yellow-800 border border-yellow-200`}>Pending</span>;
    }
    if (s === 'FAILED' || s === 'FAILURE' || s === 'DECLINED') {
      return <span className={`${base} bg-red-100 text-red-800 border border-red-200`}>Failed</span>;
    }
    return <span className={`${base} bg-gray-100 text-gray-700 border border-gray-200`}>{status || 'N/A'}</span>;
  };

  const TypeBadge = ({ type }) => {
    const t = (type || '').toUpperCase();
    if (t === 'CREDIT') {
      return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">CREDIT</span>;
    }
    if (t === 'DEBIT') {
      return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">DEBIT</span>;
    }
    return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-700 border border-gray-200">{type || '-'}</span>;
  };

  const columns = [
    {
      label: 'Transaction Date',
      render: (r) => {
        const d = r.transaction_date || r.txn_date || r.transactionDate || r.createdAt || r.dateTime;
        if (!d || !moment(d).isValid()) return 'N/A';
        const m = moment(d).local();
        // Date on the first line, time stacked directly below it.
        return (
          <div className="leading-tight">
            <div>{m.format('DD MMMM, YYYY')}</div>
            <div className="text-xs text-gray-500">{m.format('hh:mm A')}</div>
          </div>
        );
      },
    },
    {
      label: 'Vehicle Number',
      tdClassName: '!whitespace-normal',
      render: (r) => (
        <span className="block max-w-[140px] break-words leading-snug font-semibold text-gray-900">
          {r.vehicle_number || r.vehicle_no || r.vrn || 'N/A'}
        </span>
      ),
    },
    {
      label: 'Type',
      render: (r) => <TypeBadge type={r.type} />,
    },
    {
      label: 'Amount',
      className: '[&>div]:!justify-end',
      tdClassName: 'text-right font-semibold',
      render: (r) => {
        const amt = r.amount != null ? r.amount : (r.debit_amount != null ? r.debit_amount : r.txn_amount);
        return amt != null ? `₹ ${Number(amt).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : 'N/A';
      },
    },
    {
      label: 'Status',
      render: (r) => <StatusBadge status={r.status || r.txn_status || r.state} />,
    },
    showDistributor && {
      label: 'Distributor Name',
      tdClassName: '!whitespace-normal',
      render: (r) => (
        <span className="block max-w-[160px] break-words leading-snug">{r.distributor_name || '-'}</span>
      ),
    },
    showDealer && {
      label: 'Dealer Name',
      tdClassName: '!whitespace-normal',
      render: (r) => (
        <span className="block max-w-[160px] break-words leading-snug">{r.dealer_name || '-'}</span>
      ),
    },
    showAgent && {
      label: 'Agent Name',
      tdClassName: '!whitespace-normal',
      render: (r) => (
        <span className="block max-w-[160px] break-words leading-snug">{r.agent_name || '-'}</span>
      ),
    },
    {
      label: 'MERCHANT/TOLL NAME',
      tdClassName: 'text-right !whitespace-normal',
      render: (r) => (
        <span className="inline-block max-w-[220px] text-right break-words leading-snug">
          {r.merchant || r.plaza_name || r.toll_plaza || r.plazaName || r.description || '-'}
        </span>
      ),
    },
  ].filter(Boolean); // org columns above are role-gated and drop out as `false`

  const clearFilters = async () => {
    setFromDate(null);
    setToDate(null);
    setVehicleNumber('');
    setDebouncedVehicle('');
    setSelectedDistributor('');
    setSelectedDealer('');
    setSelectedAgent('');
    setCurrentPage(1);
    try {
      if (isAdmin) {
        const [dl, ag] = await Promise.all([API.GetAllDealersNames({}), API.GetAgentsNames({})]);
        setDealers(asList(dl));
        setAgents(asList(ag));
      } else if (role === 'distributor') {
        const [dl, ag] = await Promise.all([API.GetAllDealersNames(ownId ? { distributorId: ownId } : {}), API.GetAgentsNames({})]);
        setDealers(asList(dl));
        setAgents(asList(ag));
      } else if (role === 'dealer') {
        setAgents(asList(await API.GetAgentsNames({})));
      }
    } catch (e) { /* ignore */ }
  };

  // Dropdown options with ONLY name/username (no mobile number)
  const distributorOptions = [
    { name: 'All Distributors', value: '' },
    ...distributors.map((d, index) => ({
      name: d?.username || d?.companyName || d?.name || `Distributor ${index + 1}`,
      value: d?._id || d?.id || '',
    })),
  ];

  const dealerOptions = [
    { name: 'All Dealers', value: '' },
    ...dealers.map((dl, index) => ({
      name: dl?.username || dl?.companyName || dl?.name || `Dealer ${index + 1}`,
      value: dl?._id || dl?.id || '',
    })),
  ];

  const agentOptions = [
    { name: 'All Agents', value: '' },
    ...agents.map((ag, index) => ({
      name: ag?.username || ag?.name || `Agent ${index + 1}`,
      value: ag?._id || ag?.id || '',
    })),
  ];

  // Pull every page matching the current filters for the statement export.
  const fetchAllForExport = async () => {
    const all = [];
    let reportedTotal = null;
    for (let page = 1; page <= EXPORT_MAX_PAGES; page++) {
      const res = await API.GetNewDebitTransactions({
        from: fromDate ? moment(fromDate).format('YYYY-MM-DD') : '',
        to: toDate ? moment(toDate).format('YYYY-MM-DD') : '',
        page,
        page_size: EXPORT_PAGE_SIZE,
        vehicle_number: debouncedVehicle,
        distributorId: selectedDistributor || (role === 'distributor' ? ownId : ''),
        dealerId: selectedDealer || (role === 'dealer' ? ownId : ''),
        agentId: selectedAgent || (role === 'agent' ? ownId : ''),
      });
      if (!(res?.status === 200 && res?.success)) break;
      const { list, count } = extractRows(res);
      if (reportedTotal === null) reportedTotal = count;
      all.push(...list);
      if (list.length < EXPORT_PAGE_SIZE) break;
      if (reportedTotal !== null && all.length >= reportedTotal) break;
    }
    return { records: all, reportedTotal: reportedTotal === null ? all.length : reportedTotal };
  };

  const labelOf = (options, value) => options.find((o) => o.value === value)?.name || '';

  const handleDownload = async (format) => {
    setMenuOpen(false);
    if (downloading) return;
    setDownloading(true);
    try {
      const { records, reportedTotal } = await fetchAllForExport();
      if (!records.length) {
        ErrorToast('No transactions to download');
        return;
      }
      const meta = {
        from: fromDate,
        to: toDate,
        vehicle: debouncedVehicle,
        show: { distributor: showDistributor, dealer: showDealer, agent: showAgent },
        distributor: labelOf(distributorOptions, selectedDistributor),
        dealer: labelOf(dealerOptions, selectedDealer),
        agent: labelOf(agentOptions, selectedAgent),
      };
      if (format === 'excel') downloadStatementExcel(records, meta);
      else await downloadStatementPdf(records, meta);

      // Never truncate silently — say so if the page cap cut the export short.
      if (reportedTotal > records.length) {
        WarningToast(`Downloaded the first ${records.length} of ${reportedTotal} transactions. Narrow the filters for the rest.`);
      } else {
        SuccessToast(`Statement downloaded (${records.length} transactions)`);
      }
    } catch (err) {
      ErrorToast('Failed to download statement');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-6 mt-6">
        <div className="flex items-center gap-3">
          <span className="w-1.5 h-7 rounded-full bg-[#EE3B31]"></span>
          <h2 className="text-2xl font-bold text-gray-900 leading-tight">Transaction</h2>
        </div>

        {/* Download Statement (Excel / PDF) — hidden when there is nothing to export. */}
        {rows.length > 0 && (
        <div className="relative shrink-0" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            disabled={downloading}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="h-[42px] px-4 flex items-center gap-2 rounded-lg bg-[#EE3B31] text-white text-sm font-semibold shadow-sm transition-all duration-200 hover:bg-[#d43229] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            <span className="hidden sm:inline">{downloading ? 'Preparing...' : 'Download Statement'}</span>
          </button>

          {menuOpen && !downloading && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden z-20"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => handleDownload('excel')}
                className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                Excel
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => handleDownload('pdf')}
                className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-gray-700 border-t border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <FileText className="w-4 h-4 text-[#EE3B31]" />
                PDF
              </button>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Filters Section (Single Line Row) */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-6">
        <div className="flex flex-col xl:flex-row gap-3 items-stretch xl:items-end">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-3 flex-1 items-end">
            {/* Vehicle Number Filter */}
            <div className="relative">
              <TextInput
                name="vehicleNumber"
                label="Vehicle Number"
                labelClass="!text-xs !text-gray-500 !font-medium mb-1"
                placeholder="e.g. RJ52GB1501"
                value={vehicleNumber}
                onChange={(e) => {
                  setVehicleNumber(e.target.value);
                  setCurrentPage(1);
                }}
                inputClass="h-[42px] !py-0 pl-3 pr-9 text-xs rounded-lg uppercase placeholder:normal-case"
              />
              <div className="absolute right-3 bottom-[13px] text-gray-400 pointer-events-none">
                <Search className="w-4 h-4" />
              </div>
            </div>

            {/* Distributor Filter (admin only) */}
            {showDistributor && (
              <div>
                <CustomSelectInput
                  name="distributor"
                  label="Distributor"
                  labelClass="text-xs text-gray-500 font-medium mb-1"
                  value={selectedDistributor}
                  onChange={(e) => handleDistributorChange(e.target.value)}
                  options={distributorOptions}
                  showSearch={true}
                  inputClass="h-[42px] px-3 text-xs bg-white border-gray-300 rounded-lg truncate"
                />
              </div>
            )}

            {/* Dealer Filter (admin + distributor) */}
            {showDealer && (
              <div>
                <CustomSelectInput
                  name="dealer"
                  label="Dealer"
                  labelClass="text-xs text-gray-500 font-medium mb-1"
                  value={selectedDealer}
                  onChange={(e) => handleDealerChange(e.target.value)}
                  options={dealerOptions}
                  showSearch={true}
                  inputClass="h-[42px] px-3 text-xs bg-white border-gray-300 rounded-lg truncate"
                />
              </div>
            )}

            {/* Agent Filter (admin + distributor + dealer) */}
            {showAgent && (
              <div>
                <CustomSelectInput
                  name="agent"
                  label="Agent"
                  labelClass="text-xs text-gray-500 font-medium mb-1"
                  value={selectedAgent}
                  onChange={(e) => {
                    setSelectedAgent(e.target.value);
                    setCurrentPage(1);
                  }}
                  options={agentOptions}
                  showSearch={true}
                  inputClass="h-[42px] px-3 text-xs bg-white border-gray-300 rounded-lg truncate"
                />
              </div>
            )}

            {/* From Date */}
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">From Date</label>
              <DatePicker
                selected={fromDate}
                onChange={(d) => { setFromDate(d); setCurrentPage(1); }}
                dateFormat="dd MMM, yyyy"
                placeholderText="From Date"
                isClearable
                maxDate={toDate || new Date()}
                className="w-full h-[42px] border border-gray-300 rounded-lg px-3 text-xs bg-white focus:outline-none focus:border-[#EE3B31] focus:ring-2 focus:ring-[#EE3B31]/20 transition-colors"
              />
            </div>

            {/* To Date */}
            <div>
              <label className="block text-xs text-gray-500 font-medium mb-1">To Date</label>
              <DatePicker
                selected={toDate}
                onChange={(d) => { setToDate(d); setCurrentPage(1); }}
                dateFormat="dd MMM, yyyy"
                placeholderText="To Date"
                isClearable
                minDate={fromDate || undefined}
                className="w-full h-[42px] border border-gray-300 rounded-lg px-3 text-xs bg-white focus:outline-none focus:border-[#EE3B31] focus:ring-2 focus:ring-[#EE3B31]/20 transition-colors"
              />
            </div>
          </div>

          {/* Reset Filters Button */}
          <div className="shrink-0 flex items-end">
            <button
              onClick={clearFilters}
              title="Reset Filters"
              aria-label="Reset Filters"
              className="w-full xl:w-[42px] h-[42px] border border-[#EE3B31] text-[#EE3B31] rounded-lg font-semibold transition-all duration-200 hover:bg-[#EE3B31] hover:text-white flex items-center justify-center shadow-sm hover:shadow cursor-pointer"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <CommonTable
          columns={columns}
          data={rows}
          emptyMessage={loading ? 'Loading...' : 'No Transactions Found'}
          isPagination={false}
          loading={loading}
          BottomComponent={
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-white">
              <div className="text-xs text-gray-500">
                Showing <span className="font-semibold text-gray-800">{total > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</span> to{' '}
                <span className="font-semibold text-gray-800">{Math.min(currentPage * itemsPerPage, total)}</span> of{' '}
                <span className="font-semibold text-gray-800">{total || 0}</span> transactions
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span>Show</span>
                  <select
                    className="py-1 px-2 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#EE3B31]/20 cursor-pointer"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    {pageSizeOptions(total, itemsPerPage).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-[#EE3B31] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-xs font-semibold text-gray-700 px-2">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage >= totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-[#EE3B31] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default TransactionsN;
